import React, { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCw, Home, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  /** Page-level boundary shows navigation instead of full reload */
  isPageLevel?: boolean;
  /** Reset error state when location changes */
  resetKey?: string;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

/**
 * ErrorBoundary - Catches JavaScript errors anywhere in the child component tree
 * Displays a friendly error message for seniors instead of crashing the entire app
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  componentDidUpdate(prevProps: ErrorBoundaryProps): void {
    // Reset error state when resetKey (e.g., route) changes
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false, error: null, errorInfo: null });
    }
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Log the error for debugging (in production, send to error tracking service)
    console.error('[ErrorBoundary] Caught error:', error);
    console.error('[ErrorBoundary] Error info:', errorInfo);
    
    this.setState({ errorInfo });
  }

  handleReload = (): void => {
    window.location.reload();
  };

  handleGoHome = (): void => {
    window.location.href = '/';
  };

  handleGoBack = (): void => {
    window.history.back();
  };

  handleRetryPage = (): void => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  render(): ReactNode {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Page-level error UI (stays within layout, shows navigation options)
      if (this.props.isPageLevel) {
        return (
          <div className="flex flex-col items-center justify-center min-h-[400px] p-6">
            <div className="bg-white rounded-3xl shadow-warm-lg p-8 max-w-md w-full text-center">
              {/* Error Icon */}
              <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertTriangle className="w-8 h-8 text-amber-600" />
              </div>

              {/* Error Message */}
              <h2 className="text-xl font-bold text-primary mb-2 font-serif">
                This page had a problem
              </h2>
              <p className="text-text-secondary mb-6">
                Don't worry! You can try again or go somewhere else.
              </p>

              {/* Action Buttons */}
              <div className="space-y-3">
                <button
                  onClick={this.handleRetryPage}
                  className="w-full flex items-center justify-center gap-2 px-5 py-3 bg-primary text-white rounded-2xl font-bold hover:bg-primary-dark transition-colors shadow-warm"
                >
                  <RefreshCw className="w-5 h-5" />
                  Try Again
                </button>
                
                <button
                  onClick={this.handleGoBack}
                  className="w-full flex items-center justify-center gap-2 px-5 py-3 bg-surface text-primary rounded-2xl font-bold border-2 border-primary/30 hover:border-primary transition-colors"
                >
                  <ArrowLeft className="w-5 h-5" />
                  Go Back
                </button>

                <button
                  onClick={this.handleGoHome}
                  className="w-full flex items-center justify-center gap-2 px-5 py-3 bg-surface text-text-secondary rounded-2xl font-medium border border-gray-200 hover:border-gray-300 transition-colors"
                >
                  <Home className="w-5 h-5" />
                  Home
                </button>
              </div>
            </div>
          </div>
        );
      }

      // Default senior-friendly error UI (full page takeover)
      return (
        <div className="min-h-screen gradient-warm flex items-center justify-center p-6">
          <div className="bg-white rounded-3xl shadow-warm-lg p-8 max-w-md w-full text-center">
            {/* Error Icon */}
            <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <AlertTriangle className="w-10 h-10 text-amber-600" />
            </div>

            {/* Error Message */}
            <h1 className="text-2xl font-bold text-primary mb-3 font-serif">
              Oops! Something went wrong
            </h1>
            <p className="text-text-secondary mb-6 text-lg">
              Don't worry, this happens sometimes. Let's try to fix it together.
            </p>

            {/* Action Buttons */}
            <div className="space-y-3">
              <button
                onClick={this.handleReload}
                className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-primary text-white rounded-2xl font-bold text-lg hover:bg-primary-dark transition-colors shadow-warm"
              >
                <RefreshCw className="w-5 h-5" />
                Try Again
              </button>
              
              <button
                onClick={this.handleGoHome}
                className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-surface text-primary rounded-2xl font-bold text-lg border-2 border-primary/30 hover:border-primary transition-colors"
              >
                <Home className="w-5 h-5" />
                Go Home
              </button>
            </div>

            {/* Help Text */}
            <p className="mt-6 text-sm text-text-muted">
              If this keeps happening, please contact us at{' '}
              <a href="mailto:support@dipps.ca" className="text-primary underline">
                support@dipps.ca
              </a>
            </p>

            {/* Technical Details (collapsed by default in production) */}
            {import.meta.env.DEV && this.state.error && (
              <details className="mt-6 text-left">
                <summary className="text-xs text-text-muted cursor-pointer">
                  Technical Details (Developer Mode)
                </summary>
                <pre className="mt-2 p-3 bg-gray-100 rounded-lg text-xs overflow-auto max-h-40">
                  {this.state.error.toString()}
                  {this.state.errorInfo?.componentStack}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * Hook-friendly wrapper for function components
 */
export const withErrorBoundary = <P extends object>(
  WrappedComponent: React.ComponentType<P>,
  fallback?: ReactNode
) => {
  return function WithErrorBoundaryWrapper(props: P) {
    return (
      <ErrorBoundary fallback={fallback}>
        <WrappedComponent {...props} />
      </ErrorBoundary>
    );
  };
};

/**
 * Page-level error boundary wrapper - resets on retry, shows navigation
 */
export const PageErrorBoundary: React.FC<{ children: ReactNode; resetKey?: string }> = ({ children, resetKey }) => {
  return (
    <ErrorBoundary isPageLevel resetKey={resetKey}>
      {children}
    </ErrorBoundary>
  );
};
